import React from 'react';
import { Chip } from '@mui/material';
import { Text, Theme, i18n, localizationMaps, tokens } from '@simplybenefits/sb-ui-library';
import { helpers } from '@simplybenefits/sb-utils';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import { Add as AddIcon, Remove as RemoveIcon } from '@mui/icons-material';

/**
 * 
 * @param {*} param0 
 * @returns 
 */
export default function Drugs({ classes, expanded, drugs, handleChange, defineCoverageType, showDependentWarning, waivedEHC, isStudent }) {

    let gradedDrugsStr = "";
    let secondMaxStr = "";

    // Generic Substitution should not use the `genericSubstitute` label as it contains Substitute at the end.
    const isGenericSubstitute = localizationMaps.toEnglish('genericSubstitute', drugs.genericSubstitute) === 'Generic Substitution';
    const genericSubstitute = isGenericSubstitute
        ? localizationMaps.fromEnglish("genericSubstitute", drugs.genericSubstitute)
        : i18n.t(tokens.labels.coverage.genericSubstitute, { genericSubstitute: localizationMaps.fromEnglish("genericSubstitute", drugs.genericSubstitute) });

    if (drugs.isGraded) {
        secondMaxStr = (drugs.secondMax > 0 && drugs.secondMax !== 'Unlimited') ? 
            i18n.t(tokens.labels.coverage.nextSecondMaxSpent, { secondMax: helpers.formatNumber(drugs.secondMax) }) 
            : 
            i18n.t(tokens.labels.coverage.remainderSpent);
        
        gradedDrugsStr = i18n.t(
            tokens.labels.coverage.gradedScaleExplanation,
            { 
                firstPercentage: drugs.firstPercentage, 
                firstMax: helpers.formatNumber(drugs.firstMax), 
                secondPercentage: drugs.secondPercentage, 
                secondMaxStr 
            }
        );
    }

    return (
        <div style={ classes.block }>
            <Accordion
                square
                onChange={handleChange('drugs')}
                style={{ boxShadow: 'none' }}
                disableGutters={true}
            >
                <AccordionSummary
                    classes={{
                        content: classes.panelContent,
                        expanded: classes.expanded,
                        root: classes.root
                    }}
                    expandIcon={expanded.includes('drugs') ? <RemoveIcon /> : <AddIcon />}
                    sx={classes.accordionSummary}
                >
                    <Text medium bold>{ i18n.t(tokens.titles.drugs) }</Text>
                    {
                        waivedEHC &&
                            <Chip
                                size="small"
                                label="Waived"
                                color="primary"
                                variant="outlined"
                                style={{
                                    margin: "0 10px",
                                    borderRadius: 30,
                                    borderWidth: 2,
                                    borderColor: Theme.colors.lightGrey,
                                    fontFamily: Theme.fonts.type.heading,
                                    fontSize: 14,
                                    color: '#666'
                                }}
                            />
                    }
                </AccordionSummary>
                <AccordionDetails style={classes.panelDetails}>
                    {
                        drugs.isEligible !== 1 ?
                            <div style={classes.row}>
                                <Text small bold margin="20px 0px 10px 0px">
                                    { i18n.t(tokens.labels.coverage.ineligibleForBenefit)}
                                </Text>
                            </div>
                            : 
                            null
                    }

                    {
                        showDependentWarning &&
                            <div style={classes.row}>
                                <Text small bold margin="20px 0px 10px 0px">
                                    { i18n.t(tokens.labels.coverage.waivedBenefitForDependents) }
                                </Text>
                            </div>
                    }

                    {
                        drugs.isGraded ?
                            <Text small bold margin="5px 0px 10px 0px">{ gradedDrugsStr }</Text>
                            :
                            <Text small bold margin="5px 0px 10px 0px">{ i18n.t(tokens.labels.coverage.reimbursementOf, { reimbursement: drugs.reimbursement }) }</Text>
                    }

                    {
                        drugs.isPreferredProvider ?
                            <React.Fragment>
                                <Text small bold margin="5px 0px 0px 0px">{ i18n.t(tokens.labels.coverage.preferredProviderReimbursement, { preferredProviderReimbursement: drugs.preferredProviderReimbursement }) }</Text>
                                <Text small bold margin="5px 0px 0px 0px">{ i18n.t(tokens.labels.coverage.preferredProvider, { preferredProvider: drugs.preferredProvider })}</Text>
                            </React.Fragment>
                            : 
                            null
                    }

                    <div>
                        {
                            (drugs.hasDeductible && !drugs.deductibleEqualToDispenseFee) ?
                                defineCoverageType(drugs, false)
                                : 
                                null
                        }

                        {
                            drugs.hasOverallMax ?
                                <Text small margin="5px 0px 0px 0px">{ i18n.t(tokens.labels.coverage.overallMaximum, { overallMax: helpers.formatNumber(drugs.overallMax) }) }</Text>
                                : 
                                null
                        }

                        <Text small margin="5px 0px 0px 0px">
                            { genericSubstitute }
                        </Text>

                        {
                            (drugs.hasDeductible && drugs.deductibleEqualToDispenseFee) ?
                                <Text small margin="5px 0px 0px 0px">{ i18n.t(tokens.labels.coverage.deductibleEqualToDispenseFee) }</Text>
                                : 
                                null
                        }

                        {
                            drugs.hasDispenseFeeMax ?
                                <Text small margin="5px 0px 0px 0px">{ i18n.t(tokens.labels.coverage.dispenseFeeMaximum, { dispenseFeeMax: helpers.formatNumber(drugs.dispenseFeeMax) })}</Text>
                                : 
                                null
                        }

                        {
                            drugs.includeErectDysfuncDrugs ?
                                <React.Fragment>
                                    <Text small margin="5px 0px 0px 0px">{ i18n.t(tokens.labels.coverage.erectileDysfunctionMaximum, { edMax: helpers.formatNumber(drugs.edMax) }) }</Text>
                                    <Text small margin="5px 0px 0px 0px">{ i18n.t(tokens.labels.coverage.erectileDysfunctionReimbursement, { edReimbursement: drugs.edReimbursement }) }</Text>
                                </React.Fragment>
                                : 
                                null
                        }

                        {
                            drugs.includeFertilityDrugs ?
                                <React.Fragment>
                                    <Text small margin="5px 0px 0px 0px">{ i18n.t(tokens.labels.coverage.fertilityDrugsMaximum, { fertilityMax: helpers.formatNumber(drugs.fertilityMax) }) }</Text>
                                    <Text small margin="5px 0px 0px 0px">{ i18n.t(tokens.labels.coverage.fertilityDrugsReimbursement, { fertilityReimbursement: drugs.fertilityReimbursement }) }</Text>
                                </React.Fragment>
                                : 
                                null
                        }

                        {
                            drugs.includePreventativeVaccines ?
                                <>
                                    {
                                        drugs.preventativeVaccinesIsRC ?
                                            <>
                                                <Text small margin="5px 0px 0px 0px">{ i18n.t(tokens.labels.coverage.customaryExpensesForPreventativeVaccines) }</Text>
                                                <Text small margin="5px 0px 0px 0px">{ i18n.t(tokens.labels.coverage.preventativeVaccinesReimbursement, { preventativeVaccinesReimbursement: drugs.preventativeVaccinesReimbursement }) }</Text>
                                            </>
                                            :
                                            <>
                                                <Text small margin="5px 0px 0px 0px">{ i18n.t(tokens.labels.coverage.preventativeVaccinesMaximum, { preventativeVaccinesMax: helpers.formatNumber(drugs.preventativeVaccinesMax) }) }</Text>
                                                <Text small margin="5px 0px 0px 0px">{ i18n.t(tokens.labels.coverage.preventativeVaccinesReimbursement, { preventativeVaccinesReimbursement: drugs.preventativeVaccinesReimbursement }) }</Text>
                                            </>
                                    }
                                </>
                                : null
                        }

                        {
                            drugs.includeSmokingCessations ?
                                <React.Fragment>
                                    <Text small margin="5px 0px 0px 0px">{ i18n.t(tokens.labels.coverage.smokingCessationMaximum, { smokingCessationsMax: helpers.formatNumber(drugs.smokingCessationsMax) }) }</Text>
                                    <Text small margin="5px 0px 0px 0px">{ i18n.t(tokens.labels.coverage.smokingCessationReimbursement, { smokingCessationsReimbursement: drugs.smokingCessationsReimbursement }) }</Text>
                                </React.Fragment>
                                : 
                                null
                        }

                        {
                            isStudent ?
                                <Text small margin="5px 0px 0px 0px">{ i18n.t(tokens.labels.coverage.terminatesAtEndOfStudy, { terminationAge: drugs.terminationAge }) }</Text>
                                :
                                <Text small margin="5px 0px 0px 0px">{ i18n.t(tokens.labels.coverage.terminatesAtAge, { terminationAge: drugs.terminationAge }) }</Text>
                        }
                    </div>

                    <Text small bold margin="10px 0px 0px 0px">
                        { i18n.t(tokens.labels.coverage.drugsAlternativeCoverage) }
                    </Text>
                </AccordionDetails>
            </Accordion>
        </div>
    );
}